// Implements: F-UIX-019
/**
 * The platform admin's status panel (UIX.8, OBS.12): one answer of `GET /admin/status`.
 *
 * - The summary: the database, the provider probe (word, when it was checked, its latency),
 *   whether the usage limits apply and whether the per-minute limiters run without their
 *   shared store (since when; this replica's view).
 * - The outbox: pending events, how long the oldest has waited, failed events.
 * - The scheduled jobs: interval, last and next run, "Atrasada" once a whole interval has
 *   passed since the run it was due (judged against the server's `read_at`: the job is not being
 *   run), and how the last run ended. A job whose last run failed still moves its last and next
 *   run, so it is flagged by its outcome ("Falló", with the error's class name, never a message)
 *   and counted above the table (7.S13.11).
 * - The model health of the last hour: calls and FAILED STEPS per model and step role. A
 *   failed step is one the model answered and that ended in error; provider timeouts and 5xx
 *   answers leave no step, so they show in the provider line, never as a model's rate.
 *
 * Operational figures only; no person, content or amount. Rows are keyed by job name and by
 * model plus step role.
 *
 * The redesign (UX-11) draws it as the content of `/plataforma/estado`: the page owns the title,
 * so the panel is a region named by it with no heading of its own; the summary is the mockup's
 * status list (one row per part, the state as a toned badge) and each section is a card.
 */

import { useId } from "react";
import type { ReactElement, ReactNode } from "react";

import { useTranslation } from "../../../shared/i18n";
import type { TranslationKey } from "../../../shared/i18n";
import { Badge } from "../../../shared/ui/atoms/Badge";
import type { BadgeTone } from "../../../shared/ui/atoms/Badge";
import { Button } from "../../../shared/ui/atoms/Button";
import { Card, CardHeader } from "../../../shared/ui/atoms/Card";
import type { JobStatusView, ProviderCreditsView, ProviderStatusView, SystemStatusView } from "../../../types/status";
import { costText } from "../model/money";
import { durationText, instantText, isBehind, rateText } from "../model/status";
import styles from "./StatusPanel.module.css";
import { countText } from "./TotalsRow";

export interface StatusPanelProps {
  readonly view: SystemStatusView;
  readonly refreshing: boolean;
  /** The last read failed: the answer on screen is the previous one. */
  readonly refreshFailed: boolean;
  readonly onRefresh: () => void;
}

/** The words of the jobs the platform registers (`platform/infrastructure/entrypoint.py`). */
const JOB_LABELS = {
  heartbeat: "admin.status.job.heartbeat",
  personal_data_purge: "admin.status.job.personalDataPurge",
  mockup_trash_purge: "admin.status.job.mockupTrashPurge",
  version_retention_purge: "admin.status.job.versionRetentionPurge",
  share_retention_purge: "admin.status.job.shareRetentionPurge",
  outbox_dispatch: "admin.status.job.outboxDispatch",
  usage_global_alert: "admin.status.job.usageGlobalAlert",
  provider_probe: "admin.status.job.providerProbe",
  generation_stale_close: "admin.status.job.generationStaleClose",
  usage_daily_aggregate: "admin.status.job.usageDailyAggregate",
} as const satisfies Readonly<Record<string, TranslationKey>>;

/** The words of a job, or `undefined` for a job this screen does not know (named by its own name). */
function jobLabel(name: string): (typeof JOB_LABELS)[keyof typeof JOB_LABELS] | undefined {
  // Own keys only: a job named like an `Object.prototype` member is still a job this screen does not know.
  return Object.hasOwn(JOB_LABELS, name) ? JOB_LABELS[name as keyof typeof JOB_LABELS] : undefined;
}

const PROVIDER = {
  ok: ["admin.status.provider.ok", "success"],
  failing: ["admin.status.provider.failing", "danger"],
  unknown: ["admin.status.provider.unknown", "neutral"],
} as const satisfies Readonly<Record<ProviderStatusView["status"], readonly [TranslationKey, BadgeTone]>>;

/** The provider's word and tone; a word this screen does not know reads as "no data". */
function providerBadge(status: string): (typeof PROVIDER)[keyof typeof PROVIDER] {
  return Object.hasOwn(PROVIDER, status) ? PROVIDER[status as keyof typeof PROVIDER] : PROVIDER.unknown;
}

const CREDITS = {
  ok: ["admin.status.credits.state.ok", "success"],
  low: ["admin.status.credits.state.low", "warning"],
  unknown: ["admin.status.credits.state.unknown", "neutral"],
} as const satisfies Readonly<Record<ProviderCreditsView["state"], readonly [TranslationKey, BadgeTone]>>;

/** The balance verdict's word and tone; a word this screen does not know reads as "unknown". */
function creditsBadge(state: string): (typeof CREDITS)[keyof typeof CREDITS] {
  return Object.hasOwn(CREDITS, state) ? CREDITS[state as keyof typeof CREDITS] : CREDITS.unknown;
}

function msText(value: number): string {
  return `${countText(value)} ms`;
}

function Figure({ term, children }: { readonly term: string; readonly children: ReactNode }): ReactElement {
  return (
    <div className={styles.figure}>
      <dt className={styles.term}>{term}</dt>
      <dd className={styles.value}>{children}</dd>
    </div>
  );
}

function Section({ title, children }: { readonly title: string; readonly children: ReactNode }): ReactElement {
  const heading = useId();
  return (
    <Card as="section" labelledBy={heading}>
      <CardHeader>
        <h2 id={heading} className={styles.subtitle}>
          {title}
        </h2>
      </CardHeader>
      <div className={styles.section}>{children}</div>
    </Card>
  );
}

/** How a last run reads; a word this screen does not know reads "Sin datos", never as sent. */
function JobOutcomeCell({ job }: { readonly job: JobStatusView }): ReactElement {
  const { t } = useTranslation();
  switch (job.last_outcome) {
    case null:
      return <span className={styles.muted}>{t("admin.status.jobs.outcome.none")}</span>;
    case "succeeded":
      return <Badge tone="success">{t("admin.status.jobs.outcome.succeeded")}</Badge>;
    case "failed":
      return (
        <>
          <Badge tone="danger">{t("admin.status.jobs.outcome.failed")}</Badge>
          {job.last_error === null ? null : (
            <>
              {" "}
              <span className={styles.token}>{job.last_error}</span>
            </>
          )}
        </>
      );
    default:
      return <span className={styles.muted}>{t("admin.status.jobs.outcome.unknown")}</span>;
  }
}

function JobRow({ job, readAt }: { readonly job: JobStatusView; readonly readAt: string | null }): ReactElement {
  const { t } = useTranslation();
  const label = jobLabel(job.name);
  const behind = isBehind(job, readAt);
  return (
    <tr>
      <th scope="row">
        {label === undefined ? null : `${t(label)} `}
        <span className={styles.token}>{job.name}</span>
      </th>
      <td>{durationText(job.interval_seconds)}</td>
      <td>{instantText(job.last_run_at) ?? t("admin.status.jobs.never")}</td>
      <td>{instantText(job.next_run_at) ?? t("admin.status.unknown")}</td>
      <td>
        <Badge tone={behind ? "danger" : "success"}>
          {t(behind ? "admin.status.jobs.behind" : "admin.status.jobs.onTime")}
        </Badge>
      </td>
      <td>
        <JobOutcomeCell job={job} />
      </td>
    </tr>
  );
}

export function StatusPanel({ view, refreshing, refreshFailed, onRefresh }: StatusPanelProps): ReactElement {
  const { t } = useTranslation();
  const [providerWord, providerTone] = providerBadge(view.provider.status);
  const checkedAt = instantText(view.provider.checked_at);
  const credits = view.provider_credits;
  const [creditsWord, creditsTone] = creditsBadge(credits.state);
  const creditsCheckedAt = instantText(credits.checked_at);
  const readAt = instantText(view.read_at);
  const failing = view.jobs.filter((job) => job.last_outcome === "failed").length;
  const limiterSince = instantText(view.limiter_unavailable_since);
  const modelsSince = instantText(view.model_health.since);

  return (
    <section className={styles.panel} aria-label={t("admin.status.title")}>
      <div className={styles.header}>
        {readAt === null ? null : <p className={styles.muted}>{t("admin.status.readAt", { at: readAt })}</p>}
        <Button variant="secondary" size="sm" loading={refreshing} onClick={onRefresh}>
          {t("admin.status.refresh")}
        </Button>
      </div>
      {refreshFailed ? (
        <p className={styles.stale} role="status">
          {t("admin.status.refreshFailed")}
        </p>
      ) : null}

      <Card>
        <dl className={styles.statusList} aria-label={t("admin.status.summaryLabel")}>
          <Figure term={t("admin.status.database")}>
            <Badge tone={view.database === "ok" ? "success" : "danger"}>
              {t(view.database === "ok" ? "admin.status.database.ok" : "admin.status.database.unavailable")}
            </Badge>
          </Figure>
          <Figure term={t("admin.status.provider")}>
            <Badge tone={providerTone}>{t(providerWord)}</Badge>{" "}
            <span className={styles.muted}>
              {checkedAt === null
                ? t("admin.status.provider.never")
                : t("admin.status.provider.checked", { at: checkedAt })}
              {view.provider.latency_ms === null ? null : ` · ${msText(view.provider.latency_ms)}`}
            </span>
          </Figure>
          <Figure term={t("admin.status.caps")}>
            <Badge tone={view.caps_disabled ? "danger" : "success"}>
              {t(view.caps_disabled ? "admin.status.caps.off" : "admin.status.caps.on")}
            </Badge>
          </Figure>
          <Figure term={t("admin.status.limiter")}>
            {limiterSince === null ? (
              <Badge tone="success">{t("admin.status.limiter.ok")}</Badge>
            ) : (
              <>
                <Badge tone="warning">{t("admin.status.limiter.down")}</Badge>{" "}
                <span className={styles.muted}>{t("admin.status.limiter.since", { at: limiterSince })}</span>
              </>
            )}
          </Figure>
        </dl>
      </Card>

      <Section title={t("admin.status.credits.title")}>
        <dl className={styles.figures}>
          <Figure term={t("admin.status.credits.remaining")}>
            {credits.remaining_micros === null ? (
              <span className={styles.muted}>{t("admin.status.credits.unavailable")}</span>
            ) : (
              costText(credits.remaining_micros, credits.currency)
            )}
          </Figure>
          <Figure term={t("admin.status.credits.state")}>
            <Badge tone={creditsTone}>{t(creditsWord)}</Badge>
          </Figure>
          <Figure term={t("admin.status.credits.threshold")}>
            {costText(credits.threshold_micros, credits.currency)}
          </Figure>
          <Figure term={t("admin.status.credits.checked")}>
            {creditsCheckedAt ?? t("admin.status.provider.never")}
          </Figure>
        </dl>
      </Section>

      <Section title={t("admin.status.outbox.title")}>
        <dl className={styles.figures}>
          <Figure term={t("admin.status.outbox.pending")}>{countText(view.outbox.pending)}</Figure>
          <Figure term={t("admin.status.outbox.oldest")}>
            {view.outbox.oldest_pending_age_seconds === null
              ? t("admin.status.outbox.none")
              : durationText(view.outbox.oldest_pending_age_seconds)}
          </Figure>
          <Figure term={t("admin.status.outbox.failed")}>
            <span className={view.outbox.failed > 0 ? styles.alarm : undefined}>{countText(view.outbox.failed)}</span>
          </Figure>
        </dl>
      </Section>

      <Section title={t("admin.status.jobs.title")}>
        {failing === 0 ? null : (
          <p className={styles.failing}>{t("admin.status.jobs.failing", { count: countText(failing) })}</p>
        )}
        {view.jobs.length === 0 ? (
          <p className={styles.muted}>{t("admin.status.jobs.empty")}</p>
        ) : (
          <div className={styles.scroll}>
            <table className={styles.table} aria-label={t("admin.status.jobs.title")}>
              <thead>
                <tr>
                  <th scope="col">{t("admin.status.jobs.name")}</th>
                  <th scope="col">{t("admin.status.jobs.interval")}</th>
                  <th scope="col">{t("admin.status.jobs.lastRun")}</th>
                  <th scope="col">{t("admin.status.jobs.nextRun")}</th>
                  <th scope="col">{t("admin.status.jobs.state")}</th>
                  <th scope="col">{t("admin.status.jobs.outcome")}</th>
                </tr>
              </thead>
              <tbody>
                {view.jobs.map((job) => (
                  <JobRow key={job.name} job={job} readAt={view.read_at} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title={t("admin.status.models.title")}>
        {modelsSince === null ? null : (
          <p className={styles.muted}>{t("admin.status.models.since", { at: modelsSince })}</p>
        )}
        <p className={styles.muted}>{t("admin.status.models.note")}</p>
        {view.model_health.models.length === 0 ? (
          <p className={styles.muted}>{t("admin.status.models.empty")}</p>
        ) : (
          <div className={styles.scroll}>
            <table className={styles.table} aria-label={t("admin.status.models.title")}>
              <thead>
                <tr>
                  <th scope="col">{t("admin.status.models.model")}</th>
                  <th scope="col">{t("admin.status.models.role")}</th>
                  <th scope="col">{t("admin.status.models.calls")}</th>
                  <th scope="col">{t("admin.status.models.failedSteps")}</th>
                  <th scope="col">{t("admin.status.models.rate")}</th>
                  <th scope="col">{t("admin.status.models.median")}</th>
                  <th scope="col">{t("admin.status.models.tail")}</th>
                </tr>
              </thead>
              <tbody>
                {view.model_health.models.map((entry) => (
                  <tr key={`${entry.model} ${entry.step_role}`}>
                    <th scope="row">{entry.model}</th>
                    <td>{entry.step_role}</td>
                    <td>{countText(entry.calls)}</td>
                    <td>{countText(entry.failed_steps)}</td>
                    <td>{rateText(entry.failed_step_rate)}</td>
                    <td>{msText(entry.p50_ms)}</td>
                    <td>{msText(entry.p95_ms)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </section>
  );
}
